import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { CustomCalendarProps } from '../components/CustomCalendar/CustomCalendar';
import { useAppDataContext } from '../contexts/appData';
import { useIotsApi } from '../contexts/iotsApi';
import {
  AllowedCalendarRangeTypes,
  EntityStatus,
  JourneyStatus,
} from '../types/core';
import { DAYJS_DATA_DATE_FORMAT, getMonthRange } from '../utils/date';
import useQueryErrorNotifier from './useQueryErrorNotifier';
import useSessionCalendarState, {
  InitialCalendarState,
} from './useSessionCalendarState';

export interface CustomCalendarManager {
  isLoading: boolean;
  customCalendarProps: CustomCalendarProps;
}

export interface CustomCalendarManagerInitialState {
  calendarState?: InitialCalendarState;
  journeyStatusFilters?: JourneyStatus[];
  entityStatusFilters?: EntityStatus[];
}

const useCustomCalendarManager = (
  sessionKey: string,
  rangeType: AllowedCalendarRangeTypes,
  initialState?: CustomCalendarManagerInitialState,
): CustomCalendarManager => {
  const { getContracts, getJourneyPlaceholders } = useIotsApi();
  const { portsMap } = useAppDataContext();

  const { calendarState, setCalendarState } = useSessionCalendarState(
    sessionKey,
    rangeType,
    initialState?.calendarState,
  );

  const [statusFilters, setStatusFilters] = useState<{
    journeyStatus: JourneyStatus[];
    entityStatus: EntityStatus[];
  }>({
    journeyStatus: initialState?.journeyStatusFilters ?? [],
    entityStatus: initialState?.entityStatusFilters ?? [],
  });

  const {
    data: contracts = [],
    isFetching: isFetchingContracts,
    error: contractsError,
  } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => getContracts(),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const { selectedContractId, selectedDate } = calendarState;

  const {
    data: journeyPlaceholders = [],
    isFetching: isFetchingJourneyPlaceholders,
    isLoading: isLoadingJourneyPlaceholders,
    error: journeyPlaceholdersError,
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      'journeys',
      selectedContractId ?? '',
      ...(rangeType === AllowedCalendarRangeTypes.Monthly
        ? ['monthly', selectedDate.year(), selectedDate.month()]
        : [
            'daily',
            selectedDate.year(),
            selectedDate.month(),
            selectedDate.date(),
          ]),
    ],
    queryFn: () => {
      if (!selectedContractId) {
        return Promise.resolve([]);
      }

      const [startDate, endDate] =
        rangeType === AllowedCalendarRangeTypes.Monthly
          ? getMonthRange(selectedDate.toDate())
          : [
              selectedDate.format(DAYJS_DATA_DATE_FORMAT),
              selectedDate.format(DAYJS_DATA_DATE_FORMAT),
            ];

      return getJourneyPlaceholders(startDate, endDate, selectedContractId);
    },
  });

  useQueryErrorNotifier([contractsError, journeyPlaceholdersError]);

  return {
    isLoading: isFetchingContracts || isFetchingJourneyPlaceholders,
    customCalendarProps: {
      rangeType,
      contracts: contracts,
      entityStatusFilters: statusFilters.entityStatus,
      isFetchingContracts: isFetchingContracts,
      isLoadingJourneyPlaceholders: isLoadingJourneyPlaceholders,
      journeyPlaceholders: journeyPlaceholders,
      journeyStatusFilters: statusFilters.journeyStatus,
      onEntityStatusFiltersChange: newFilters =>
        setStatusFilters(prev => ({ ...prev, entityStatus: newFilters })),
      onJourneyStatusFiltersChange: newFilters =>
        setStatusFilters(prev => ({ ...prev, journeyStatus: newFilters })),
      onSelectedContractIdChange: newValue =>
        setCalendarState(prev => ({ ...prev, selectedContractId: newValue })),
      onSelectedDateChange: newValue =>
        setCalendarState(prev => ({ ...prev, selectedDate: newValue })),
      onSelectedViewChange: newValue =>
        setCalendarState(prev => ({ ...prev, selectedView: newValue })),
      selectedContractId: calendarState.selectedContractId,
      selectedDate: calendarState.selectedDate,
      selectedView: calendarState.selectedView,
      portsMap,
    },
  };
};

export default useCustomCalendarManager;
